/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useMemo, useContext } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LocationTargetingSettingPanel, useIsMounted } from '@bingads-webui-campaign-react/step-wizard-controls';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import {
  Collapse,
  useCollapseState,
  collapseExpander,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import {
  CampaignNamePanel,
  BudgetPanel,
  BidStrategyPanel,
  BidValuePanel,
  LanguageTargetPanel,
  AdSchedulePanel,
  UrlOptionsPanel,
  AppDetailsPanel,
  PANEL_KEYS,
} from '@bingads-webui-campaign-react/wizard-shared-components';
import { CampaignValidationGroup } from '@bingads-webui-campaign-react/unified-app-shared';
import { MANUAL_CPA, MANUAL_CPC } from '@bingads-webui-campaign-react/bid-strategy-component';

import { useCampaignDetailsStepStyles } from './use-campaign-details-step-styles';

export const CampaignDetailsForm = ({
  data: campaignInfo,
  onDataChange,
  allSectionsKeys,
  initiallyExpandedSectionsKeys,
}) => {
  const {
    campaign,
    bundleCountries,
    bundleLocationCriterions,
  } = campaignInfo || {};

  const isLoading = useMemo(() => _.isEmpty(campaignInfo), [campaignInfo]);

  const getStyles = useCampaignDetailsStepStyles();

  const i18n = useContext(I18nContext);
  const isMounted = useIsMounted();
  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);

  const [
    allSections,
    expandedSections,
    setExpandedSections,
  ] = useCollapseState(allSectionsKeys, initiallyExpandedSectionsKeys);

  const expand = useMemo(
    () => collapseExpander({ setActiveKeys: setExpandedSections }),
    [setExpandedSections]
  );

  const isBidStrategyCPC = useMemo(() => !isLoading && campaign?.BiddingScheme.Type === 'ManualCpc', [isLoading, campaign]);
  const getBidStrategyOptions = useMemo(() => (isBidStrategyCPC ? [MANUAL_CPC] : [MANUAL_CPA]), [isBidStrategyCPC]);

  return (
    <div className={getStyles('formContainer')}>
      <CampaignValidationGroup
        data={campaign}
        onDataChange={onDataChange}
        isValidating={isMounted.current}
        i18n={legacyI18n}
      >
        <Collapse
          allPanelItems={allSections}
          checkItems
          skipItemsTypeCheck
          activeKeys={expandedSections}
          setActiveKeys={setExpandedSections}
        >
          <CampaignNamePanel
            key={PANEL_KEYS.CAMPAIGN_NAME}
            isLoading={isLoading}
          />
          <AppDetailsPanel
            key={PANEL_KEYS.APP}
            isLoading={isLoading}
            data={campaignInfo}
          />
          <BidStrategyPanel
            key={PANEL_KEYS.BID_STRATEGY}
            isLoading={isLoading}
            expand={expand}
            onDataChange={onDataChange}
            data={campaignInfo}
            options={{
              bidStrategyComponentOptions: {
                bidStrategyOptions: getBidStrategyOptions,
                hideAdditionalDetails: true,
              },
              biddingBannerOptions: {
                isCampaignCreation: true,
              },
            }}
          />
          <BidValuePanel
            key={PANEL_KEYS.BID_VALUE}
            bidValueType={isBidStrategyCPC ? 'ManualCPC' : 'ManualCPI'}
            isLoading={isLoading}
            expand={expand}
            onDataChange={onDataChange}
            data={campaignInfo}
          />
          <BudgetPanel
            key={PANEL_KEYS.BUDGET}
            isLoading={isLoading}
            expand={expand}
            onDataChange={onDataChange}
            data={campaignInfo}
          />
          <LocationTargetingSettingPanel
            key={PANEL_KEYS.LOCATIONS}
            isLoading={isLoading}
            options={{
              bundleCountries,
              bundleLocationCriterions,
            }}
            expand={expand}
            data={campaign}
          />
          <LanguageTargetPanel
            key={PANEL_KEYS.LANGUAGE_TARGET}
            isLoading={isLoading}
            expand={expand}
            data={campaign}
          />
          <AdSchedulePanel
            key={PANEL_KEYS.AD_SCHEDULE}
            isLoading={isLoading}
            expand={expand}
            data={campaign}
          />
          <UrlOptionsPanel
            key={PANEL_KEYS.URL_OPTIONS}
            isLoading={isLoading}
            expand={expand}
            data={campaign}
            options={{
              showDescription: false,
              urlParameterOptions: {
                showFinalUrlSuffix: false,
                showCustomParameters: false,
              },
            }}
            isTrackingUrlTemplateRequired
            isWithError
          />
        </Collapse>
      </CampaignValidationGroup>
    </div>
  );
};

CampaignDetailsForm.propTypes = {
  data: PropTypes.shape({
    campaign: PropTypes.shape({}),
  }),
  onDataChange: PropTypes.func.isRequired,
  allSectionsKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  initiallyExpandedSectionsKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
};

CampaignDetailsForm.defaultProps = {
  data: {},
};
